import React, { useRef } from 'react';
import { Ico } from '../ui/Icons';
import { C } from '../../styles/tokens';
import { useAIVoicesStore } from '../../zustand-store/useAIVoicesStore';

// Only langCode, script watermark, and gradient are hardcoded.
// Name and voice count are derived live from the languages store.
const FEATURED = [
    { langCode: 'en-US', script: 'Aa',    grad: 'linear-gradient(135deg,#1e3a5f,#2563eb)' },
    { langCode: 'en-GB', script: 'Aa',    grad: 'linear-gradient(135deg,#1e3a8a,#60a5fa)' },
    { langCode: 'en-IN', script: 'Aa',    grad: 'linear-gradient(135deg,#312e81,#8b5cf6)' },
    { langCode: 'hi-IN', script: 'हिन्दी', grad: 'linear-gradient(135deg,#78350f,#f59e0b)' },
    { langCode: 'es-ES', script: 'Aa',    grad: 'linear-gradient(135deg,#7f1d1d,#dc2626)' },
];

export function LanguageCollections() {
    const scrollRef = useRef(null);
    const languages = useAIVoicesStore(state => state.languages);
    const setSelectedLanguage = useAIVoicesStore(state => state.setSelectedLanguage);

    const scroll = (dir) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: dir * 240, behavior: 'smooth' });
        }
    };

    // Only render cards for lang codes that are actually present in the API response
    const cards = FEATURED.filter(item => !!languages[item.langCode]);

    if (cards.length === 0) return null;

    return (
        <div style={{ marginTop: 32 }}>
            {/* Heading + scroll arrows */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: C.n900, margin: 0 }}>
                    Curated language collections
                </h2>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button
                        onClick={() => scroll(-1)}
                        style={{
                            width: 28, height: 28, borderRadius: '50%',
                            border: `1px solid ${C.n200}`, background: C.w,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                    >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="2">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    <button
                        onClick={() => scroll(1)}
                        style={{
                            width: 28, height: 28, borderRadius: '50%',
                            border: `1px solid ${C.n200}`, background: C.w,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                    >
                        <Ico n="chevR" size={11} color={C.n500} />
                    </button>
                </div>
            </div>

            {/* Scrollable cards */}
            <div
                ref={scrollRef}
                style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}
            >
                {cards.map((item) => {
                    const lang = languages[item.langCode];
                    const count = lang?.voices?.length ?? 0;
                    const name = lang?.name ?? item.langCode;

                    return (
                        <div
                            key={item.langCode}
                            onClick={() => { setSelectedLanguage(lang); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            style={{
                                flex: '0 0 auto', width: 220, height: 130,
                                borderRadius: 13, background: item.grad,
                                position: 'relative', overflow: 'hidden', cursor: 'pointer',
                            }}
                        >
                            {/* Watermark script */}
                            <div style={{
                                position: 'absolute', top: 12, left: 14,
                                fontSize: 38, fontWeight: 900,
                                color: 'rgba(255,255,255,.12)',
                                lineHeight: 1, letterSpacing: -1,
                                userSelect: 'none',
                            }}>
                                {item.script}
                            </div>

                            {/* Label */}
                            <div style={{ position: 'absolute', bottom: 16, left: 16 }}>
                                <div style={{ fontSize: 17, fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                                    {name}
                                </div>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.65)', marginTop: 4 }}>
                                    {count} voices · Top picks
                                </div>
                            </div>

                            {/* Arrow */}
                            <div style={{
                                position: 'absolute', bottom: 10, right: 10,
                                width: 26, height: 26, borderRadius: '50%',
                                background: 'rgba(255,255,255,.18)',
                                border: '1px solid rgba(255,255,255,.26)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Ico n="chevR" size={11} color="white" />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
